import React, { useState, useEffect } from 'react';
import { PlusIcon, PencilIcon, TrashIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { getApiUrl } from '../../config/api';
import ImageUpload from '../../components/ImageUpload';

const initialBanner = {
  title: '',
  description: '',
  link: '',
  media: [],
  isActive: true,
  order: 0,
  titleColor: 'white',
  descriptionColor: 'white',
  badgeColor: 'red',
  showBadge: true
};

const Banners = () => {
  const [banners, setBanners] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [banner, setBanner] = useState(initialBanner);
  const [search, setSearch] = useState('');
  const [mediaUrls, setMediaUrls] = useState([]);

  // Fetch banners
  useEffect(() => {
    fetch(getApiUrl('/api/admin/banners'), { headers: { 'x-admin': 'true' } })
      .then(res => res.json())
      .then(data => setBanners(Array.isArray(data) ? data : []));
  }, []);

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    console.log('Form field change:', name, value, type); // Debug log
    setBanner(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle media upload
  const handleMediaChange = (mediaArr) => {
    // Only accept objects with url/type/publicId
    setMediaUrls(Array.isArray(mediaArr) ? mediaArr.filter(m => m && typeof m === 'object' && m.url) : []);
  };

  // Color name to hex conversion for color picker
  const getColorHex = (colorName) => {
    const colorMap = {
      'white': '#ffffff',
      'black': '#000000',
      'red': '#dc2626',
      'blue': '#2563eb',
      'green': '#16a34a',
      'yellow': '#eab308',
      'orange': '#ea580c',
      'purple': '#9333ea',
      'pink': '#ec4899',
      'gold': '#f59e0b',
      'silver': '#94a3b8'
    };
    return colorMap[colorName] || colorName;
  };

  // Handle edit
  const handleEdit = (banner) => {
    setEditingBanner(banner);
    setBanner({
      ...banner,
      order: banner.order || 0
    });
    setMediaUrls(banner.media || []);
    setShowModal(true);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    const bannerData = { ...banner, media: mediaUrls };
    try {
      const url = editingBanner 
        ? getApiUrl(`/api/admin/banners/${editingBanner._id}`)
        : getApiUrl('/api/admin/banners');
      
      const response = await fetch(url, {
        method: editingBanner ? 'PUT' : 'POST',
        headers: {
          'x-admin': 'true',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(bannerData)
      });
      
      if (!response.ok) {
        throw new Error(`Failed to ${editingBanner ? 'update' : 'create'} banner`);
      }
      
      const newBanner = await response.json();
      if (editingBanner) {
        setBanners(prev => prev.map(b => b._id === editingBanner._id ? newBanner : b));
      } else {
      setBanners(prev => [...prev, newBanner]);
      }
      
      setShowModal(false);
      setBanner(initialBanner);
      setMediaUrls([]);
      setEditingBanner(null);
    } catch (error) {
      console.error('Error saving banner:', error);
      alert(`Failed to ${editingBanner ? 'update' : 'create'} banner. Please try again.`);
    }
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this banner?')) return;
    
    try {
      const response = await fetch(getApiUrl(`/api/admin/banners/${id}`), {
        method: 'DELETE',
        headers: { 'x-admin': 'true' }
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete banner');
      }
      
      setBanners(prev => prev.filter(b => b._id !== id));
    } catch (error) {
      console.error('Error deleting banner:', error);
      alert('Failed to delete banner. Please try again.');
    }
  };

  const openModal = () => {
    setShowModal(true);
    setBanner(initialBanner);
    setMediaUrls([]);
  };

  const closeModal = () => {
    setShowModal(false);
    setBanner(initialBanner);
    setMediaUrls([]);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Banners</h1>
        <button
          onClick={openModal}
          className="bg-blue-500 text-white px-4 py-2 rounded-lg flex items-center"
        >
          <PlusIcon className="w-5 h-5 mr-2" />
          Add Banner
        </button>
      </div>

      {/* Search bar */}
      <div className="mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Search banners..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg"
          />
          <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" />
        </div>
      </div>

      {/* Banners table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Media</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {banners.map((banner) => (
              <tr key={banner._id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  {banner.media && banner.media.length > 0 ? (
                    <div className="flex gap-2">
                      {banner.media.map((item, idx) =>
                        item.url.match(/\.(mp4|webm|ogg)$/i) ? (
                          <video key={idx} src={item.url} className="h-12 w-12 object-cover rounded" controls />
                        ) : (
                          <img key={idx} src={item.url} alt={banner.title} className="h-12 w-12 object-cover rounded" />
                        )
                      )}
                    </div>
                  ) : (
                    <div className="h-12 w-12 bg-gray-200 rounded"></div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">{banner.title}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${banner.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {banner.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">{banner.order}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button 
                    className="text-blue-500 hover:text-blue-700 mr-2"
                    onClick={() => handleEdit(banner)}
                  >
                    <PencilIcon className="w-5 h-5" />
                  </button>
                  <button 
                    className="text-red-500 hover:text-red-700"
                    onClick={() => handleDelete(banner._id)}
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Banner Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-6">
              {editingBanner ? 'Edit Banner' : 'Add New Banner'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Media</label>
                  <ImageUpload
                    onImagesChange={handleMediaChange}
                    multiple={true}
                    maxFiles={5}
                    accept="image/*,video/*"
                    className="mt-1"
                    returnMeta={true}
                    initialImages={mediaUrls}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Title</label>
                  <input
                    type="text"
                    name="title"
                    value={banner.title}
                    onChange={handleChange}
                    required
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    name="description"
                    value={banner.description}
                    onChange={handleChange}
                    required
                    rows={3}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Link</label>
                  <input
                    type="text"
                    name="link"
                    value={banner.link}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Order</label>
                  <input
                    type="number"
                    name="order"
                    value={banner.order}
                    onChange={handleChange}
                    min="0"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Title Color</label>
                    <div className="mt-1 flex items-center gap-2">
                      <input
                        type="color"
                        name="titleColor"
                        value={getColorHex(banner.titleColor)}
                        onChange={handleChange}
                        className="h-10 w-16 rounded border border-gray-300"
                      />
                      <select
                        name="titleColor"
                        value={banner.titleColor || 'white'}
                        onChange={handleChange}
                        className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      >
                        <option value="">Select Color</option>
                        <option value="white">White</option>
                        <option value="black">Black</option>
                        <option value="red">Red</option>
                        <option value="blue">Blue</option>
                        <option value="green">Green</option>
                        <option value="yellow">Yellow</option>
                        <option value="orange">Orange</option>
                        <option value="purple">Purple</option>
                        <option value="pink">Pink</option>
                        <option value="gold">Gold</option>
                        <option value="silver">Silver</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Description Color</label>
                    <div className="mt-1 flex items-center gap-2">
                      <input
                        type="color"
                        name="descriptionColor"
                        value={getColorHex(banner.descriptionColor)}
                        onChange={handleChange}
                        className="h-10 w-16 rounded border border-gray-300"
                      />
                      <select
                        name="descriptionColor"
                        value={banner.descriptionColor || 'white'}
                        onChange={handleChange}
                        className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      >
                        <option value="">Select Color</option>
                        <option value="white">White</option>
                        <option value="black">Black</option>
                        <option value="red">Red</option>
                        <option value="blue">Blue</option>
                        <option value="green">Green</option>
                        <option value="yellow">Yellow</option>
                        <option value="orange">Orange</option>
                        <option value="purple">Purple</option>
                        <option value="pink">Pink</option>
                        <option value="gold">Gold</option>
                        <option value="silver">Silver</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Badge Color</label>
                    <div className="mt-1 flex items-center gap-2">
                      <input
                        type="color"
                        name="badgeColor"
                        value={getColorHex(banner.badgeColor)}
                        onChange={handleChange}
                        className="h-10 w-16 rounded border border-gray-300"
                      />
                      <select
                        name="badgeColor"
                        value={banner.badgeColor || 'red'}
                        onChange={handleChange}
                        className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      >
                        <option value="">Select Color</option>
                        <option value="red">Red</option>
                        <option value="pink">Pink</option>
                        <option value="orange">Orange</option>
                        <option value="purple">Purple</option>
                        <option value="blue">Blue</option>
                        <option value="green">Green</option>
                        <option value="yellow">Yellow</option>
                        <option value="black">Black</option>
                        <option value="white">White</option>
                        <option value="gold">Gold</option>
                        <option value="silver">Silver</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={banner.isActive}
                      onChange={handleChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-900">
                      Active
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="showBadge"
                      checked={banner.showBadge}
                      onChange={handleChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label className="m-2 block text-sm text-gray-900">
                      Show "Limited Time Offer" Badge
                    </label>
                  </div>
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingBanner(null);
                    setBanner(initialBanner);
                    setMediaUrls([]);
                  }}
                  className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                >
                  {editingBanner ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Banners; 